import { removeInkindFromGroup } from '@gik/api/inkinds/groups';
import type { BreakpointType } from '@gik/core/hooks/hooks/BreakpointHooks';
import { breakpointBem } from '@gik/core/hooks/hooks/BreakpointHooks';
import type { Group } from '@gik/core/models/gik/Group';
import type { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import repeatElement from '@gik/core/utils/repeatElement';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import type { InkindAlgoliaObject, InkindSearchFilter } from '@gik/search/components/useSearch';
import { useSearch } from '@gik/search/components/useSearch';
import { Grid } from '@gik/ui-legacy/Grid';
import { UI } from '@gik/ui-legacy/UIManager';
import type { Hit } from '@algolia/client-search';
import React from 'react';
import { InkindGroupCard } from './InkindGroupCard';
import { InkindGroupEmptyView } from './InkindGroupEmptyView.tsx';

/** A grid tile is either an Algolia search hit or an optimistically-added Page. */
type GridItem = Hit<InkindAlgoliaObject> | InkindPageAPIModel;

/** Resolves the routeId regardless of whether the item is an Algolia hit or an API Page. */
const getRouteId = (item: GridItem): string =>
  (item as Hit<InkindAlgoliaObject>).objectID ?? (item as InkindPageAPIModel).routeId;

export type InkindGroupPagesGridProps = {
  showAllPages: boolean;
  search: string;
  group: Group;
  setSeeMoreButtonVisible(visible: boolean): void;
  setNumResults(total: number): void;
  muted?: boolean;
  /** Bump to force a re-fetch (e.g. after adding a Page to the group) — see `useSearch`. */
  refreshToken?: number;
  /** Opens the "Add Existing Page" modal and refreshes the grid — forwarded to the empty view. */
  onAddExistingPages?: () => void;
  /** Whether the current user may add/remove Pages to/from this group (backend `groups.update`). */
  canManagePages?: boolean;
  /** Whether the current user may create a brand-new Page in this group (`create inkinds`). */
  canCreatePage?: boolean;
  /** Pages just added via the modal — shown immediately while Algolia indexes them. */
  optimisticallyAddedPages?: InkindPageAPIModel[];
} & UIComponent &
  BreakpointType;

function InkindGroupPagesGridComp({
  search,
  className,
  group,
  showAllPages,
  setSeeMoreButtonVisible,
  setNumResults,
  breakpoint,
  muted = false,
  refreshToken,
  onAddExistingPages,
  canManagePages = false,
  canCreatePage = false,
  optimisticallyAddedPages,
}: InkindGroupPagesGridProps) {
  const bem = useBemCN('inkind-group-pages-grid');

  const filters: InkindSearchFilter[] = React.useMemo(() => [{ key: 'groupIds', value: group.id }], [group.id]);
  const results = useSearch<InkindAlgoliaObject>(
    search,
    'inkinds',
    filters,
    showAllPages ? 1000 : 8,
    0,
    0,
    undefined,
    refreshToken
  );

  // Pages the user just removed this session. The grid renders from Algolia,
  // which lags behind the DB, so a just-removed Page would otherwise linger
  // until re-indexing. Hide them optimistically so removal feels immediate and
  // survives the Algolia lag (a full reload — by which point Algolia is caught
  // up — resets this).
  const [removedRouteIds, setRemovedRouteIds] = React.useState<Set<string>>(() => new Set());

  // If a Page the user removed this session is added back, drop it from the
  // optimistic "removed" set so it can show again (remove → re-add in one
  // session would otherwise stay hidden).
  React.useEffect(() => {
    if (!optimisticallyAddedPages?.length) return;
    setRemovedRouteIds(prev => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      optimisticallyAddedPages.forEach(page => {
        if (next.delete(page.routeId)) changed = true;
      });
      return changed ? next : prev;
    });
  }, [optimisticallyAddedPages]);

  const handleRemove = React.useCallback(
    async (item: GridItem) => {
      // For Algolia hits the objectID is the Page's routeId (see
      // `useInkindAPIOrAlgolia`), which is what the group endpoint expects.
      const routeId = getRouteId(item);

      try {
        const response = await removeInkindFromGroup(group.id, routeId);
        if (response.status === 401 || response.status === 403) {
          UI.notifyError('You can only remove InKind Pages you manage.');
          return;
        }
        if (!response.ok) throw new Error(`Unexpected response: ${response.status}`);
      } catch {
        UI.notifyError('The InKind Page could not be removed from the group. Please try again.');
        return;
      }

      setRemovedRouteIds(prev => {
        const next = new Set(prev);
        next.add(routeId);
        return next;
      });
      UI.notifySuccess('InKind Page removed from the group.');
    },
    [group.id]
  );

  const algoliaHits = results?.hits;

  // Merge optimistic additions with the Algolia results:
  //  - drop anything the user just removed this session
  //  - drop optimistic entries Algolia has already indexed (dedupe by routeId),
  //    so a Page never shows twice as the index catches up
  //  - keep `undefined` while the first search is still loading and there's
  //    nothing optimistic to show, so the empty view doesn't flash
  const items = React.useMemo<GridItem[] | undefined>(() => {
    const optimistic = (optimisticallyAddedPages ?? []).filter(page => !removedRouteIds.has(page.routeId));

    if (algoliaHits === undefined) {
      return optimistic.length > 0 ? optimistic : undefined;
    }

    const algoliaRouteIds = new Set(algoliaHits.map(hit => hit.objectID));
    const visibleAlgolia = algoliaHits.filter(hit => !removedRouteIds.has(hit.objectID));
    const pendingOptimistic = optimistic.filter(page => !algoliaRouteIds.has(page.routeId));

    return [...pendingOptimistic, ...visibleAlgolia];
  }, [algoliaHits, optimisticallyAddedPages, removedRouteIds]);

  React.useEffect(() => {
    if (!results) return;

    if (results?.nbPages) setSeeMoreButtonVisible(results?.nbPages > 1);
    setNumResults(items?.length);
  }, [results, items, setNumResults, setSeeMoreButtonVisible]);

  if (items?.length === 0 && !muted)
    return (
      <InkindGroupEmptyView
        group={group}
        search={search}
        onAddExistingPages={onAddExistingPages}
        canManagePages={canManagePages}
        canCreatePage={canCreatePage}
      />
    );

  return (
    <Grid {...bem(undefined, breakpointBem(breakpoint), className)}>
      {!muted ? (
        items?.map(item => {
          // Only wire the remove handler when the user may manage the group's
          // Pages — the card hides its remove button when it has no handler.
          return (
            <InkindGroupCard
              key={getRouteId(item)}
              hit={item}
              onDeactivate={canManagePages ? handleRemove : undefined}
            />
          );
        })
      ) : (
        <>{repeatElement(4, <div {...bem('muted-preview')} />)}</>
      )}
    </Grid>
  );
}

export const InkindGroupPagesGrid = withComponentErrorBoundary(InkindGroupPagesGridComp);
